import { StoryTheme } from './story-theme.model';
import { Neighbourhood } from './neighbourhood.model';
import { StoryTag } from './story-tag.model';
import { StoryPerson } from './story-person.model';
import { StoryLink } from './story-link.model';
import { DateUtil } from '../../util/date.util';
import { UserInformation } from '../keycloak/user-information.model';
import { StoryStatus } from './story-status.enum';
import { StoryImage } from './story-image.model';
import { StoryVideoLink } from './story-video-link.model';
import { StoryAudioLink } from './story-audio-link.model';

export class Story {
  // Story
  public id: string;
  public collectionNumber: string;
  public isProject: boolean;
  public isActive: boolean;
  public createdAt: Date;
  public publishedAt: Date;
  public user: UserInformation;
  public author: string;
  public themeList: StoryTheme[];
  public neighbourhood: Neighbourhood;
  public storyDate: string | null = null;
  public title: string;
  public url: string;
  public shortDescription: string;
  public status: StoryStatus;
  public story: string; // HTML containing the full story
  // Location
  public location: string;
  public latitude: string;
  public longitude: string;
  // Images
  public headerImage: StoryImage;
  public carouselImages: StoryImage[];
  // Media
  public videoLinks: StoryVideoLink[];
  public audioLinks: StoryAudioLink[];
  // Metadata
  public tagList: StoryTag[] = [];
  public personList: StoryPerson[] = [];
  public linkList: StoryLink[] = [];
  public relatedStoryList: Story[] = [];
  public comments: string;

  constructor(data: any = {}) {
    this.id = data?.id ?? '';
    this.collectionNumber = data?.collectionNumber ?? '';
    this.isProject = data?.isProject ?? false;
    this.isActive = data?.isActive ?? false;
    this.createdAt = data.createdAt;
    this.publishedAt = DateUtil.createOrInitialise(data?.publishedAt);
    this.author = data?.author ?? '';
    this.themeList = data?.themeList ?? [];
    this.neighbourhood = data?.neighbourhood ?? new Neighbourhood();
    this.storyDate = data?.storyDate ?? null;
    this.title = data?.title ?? '';
    this.url = data?.url ?? '';
    this.shortDescription = data?.shortDescription ?? '';
    this.status = data?.status ?? 0;
    this.user = data?.user ?? UserInformation.createFromApi(data?.user);
    this.story = data?.story ?? '';
    // Location
    this.location = data?.location ?? '';
    this.latitude = data?.latitude ?? '0';
    this.longitude = data?.longitude ?? '0';
    // Images
    this.headerImage = data?.headerImage;
    this.carouselImages = data?.carouselImages ?? [];
    // Media
    this.videoLinks = data?.videoLinks ?? [];
    this.audioLinks = data?.audioLinks ?? [];
    // Metadata
    this.tagList = data?.tagList ?? [];
    this.personList = data?.personList ?? [];
    this.linkList = data?.linkList ?? [];
    this.relatedStoryList = data?.relatedStoryList ?? [];
    this.comments = data?.comments ?? '';
  }

  public static createFromApi(data: any = {}): Story {
    const themeList = (data?.themas ?? data?.themeList ?? []).map((themeResponse: any): StoryTheme => StoryTheme.createFromApi(themeResponse));
    const neighbourhood = Neighbourhood.createFromApi(data?.area ?? data?.neighbourhood);
    const storyDate: string | null = data?.storyDate ?? null;
    const user: UserInformation = UserInformation.createFromApi(data?.user);
    const headerImage = StoryImage.createFromApi(data.headerImage);
    const carouselImages: StoryImage[] = (data?.carouselImages ?? [])
      .map((imageData: any, index: number) => StoryImage.createFromApi({...imageData, index}))
      // Sort
      .sort((storyA: StoryImage, storyB: StoryImage): boolean => storyA.metadata.order < storyB.metadata.order)
      // Correct any order mismatch from deletions or adding with wrong order
      .map((story: StoryImage, index: number): StoryImage => {
        story.metadata.order = index;
        return story;
      });
    const videoLinks: StoryVideoLink[] = (data?.videoLinks ?? []).map((videoData: any) => StoryVideoLink.createFromApi(videoData));
    const audioLinks: StoryAudioLink[] = (data?.media ?? []).map((audioData: any) => StoryAudioLink.createFromApi(audioData));
    const personList = (data?.persons ?? data?.personList ?? []).map((personData: any) => StoryPerson.createFromApi(personData));
    const tagList = (data?.tags ?? data?.tagList ?? []).map((tagData: any) => StoryTag.createFromApi(tagData));
    const linkList = (data?.externalLinks ?? data?.linkList ?? []).map((linkData: any) => StoryLink.createFromApi(linkData));
    const relatedStoryList = (data?.relatedStories ?? data?.relatedStoryList ?? []).map((relatedStoryData: any) => Story.createFromApi(relatedStoryData));

    return new Story({
      id: data?.id ?? '',
      // Story
      collectionNumber: data?.collectionId ?? data?.collectionNumber ?? '',
      isProject: data?.isProject ?? false,
      isActive: data?.isActive ?? false,
      createdAt: DateUtil.createOrInitialise(data?.createdAt),
      publishedAt: DateUtil.createOrInitialise(data?.publishedAt),
      author: data?.author ?? '',
      themeList,
      neighbourhood,
      storyDate,
      title: data?.title ?? '',
      url: data?.url ?? '',
      shortDescription: data?.shortDescription ?? '',
      status: data?.status ?? 0,
      user,
      story: data?.story ?? data?.content ?? '',
      // Location
      location: data?.location ?? '',
      latitude: data?.lat ?? data?.latitude ?? '0',
      longitude: data?.lng ?? data?.longitude ?? '0',
      // Images
      headerImage,
      carouselImages,
      // Multimedia
      videoLinks,
      audioLinks,
      // Metadata
      tagList,
      linkList,
      personList,
      relatedStoryList,
      comments: data?.internalComment ?? data?.comments ?? '',
    });
  }

  public processForApi(publish: boolean): Record<string, any> {
    const result: Record<string, any> = {
      "collectionId": this.collectionNumber,
      "title": this.title,
      "shortDescription": this.shortDescription,
      "content": this.story,
      "author": this.author,
      "storyDate": this.storyDate,
      "location": this.location,
      "lat": this.latitude,
      "lng": this.longitude,
      "themas": this.themeList.map(theme => theme.processForApi()),
      "areaId": this.neighbourhood.id,
      "internalComment": this.comments,
      "isProject": this.isProject,
      "isActive": this.isActive,
      "publish": publish,
      "carouselImages": this.carouselImages.map(image => image.processForApi()),
      "videoLinks": this.videoLinks.map(video => video.processForApi()),
      "media": this.audioLinks.map(audio => audio.processForApi()),
      "tags": this.tagList,
      "persons": this.personList,
      "externalLinks": this.linkList.map(link => link.processForApi()),
      "relatedStories": this.relatedStoryList.map((story: Story): string => story.id),
    };
    // Header image
    if (this?.headerImage?.id?.length ?? 0 > 0) {
      result['headerImage'] = this.headerImage.processForApi();
    }
    return result;
  }
}
